/**
 * DJ-Grade Library Sync Matcher Types
 *
 * This module defines the canonical data model for cross-platform track matching.
 * There is no universal cross-platform track ID, so we build a three-tier identity system:
 *
 * 1. WORK: The song/composition (abstract - "Levels" by Avicii)
 * 2. RECORDING: A specific version/remix/master (concrete - "Levels (Original Mix)")
 * 3. ASSET: A provider-specific reference (Spotify ID, TIDAL ID, local file path)
 *
 * This separation allows proper handling of remixes, edits, and bootlegs.
 */

// =============================================================================
// CORE IDENTITY MODELS
// =============================================================================

/**
 * Work represents the abstract song/composition.
 * Multiple recordings can share the same work (original, remixes, covers).
 */
export interface Work {
  id: string; // Internal canonical ID (UUID)
  title: string; // Canonical/clean title without version info
  primaryArtists: string[]; // Main credited artists
  composers?: string[]; // Songwriters if known
  iswc?: string; // International Standard Musical Work Code (rare)
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Recording represents a specific version/remix/master of a Work.
 * This is the core matching target - we match recordings, not works.
 */
export interface Recording {
  id: string; // Internal canonical ID (UUID)
  workId: string; // Reference to parent Work
  title: string; // Full title including version info
  artists: string[]; // All credited artists on this recording
  version: RecordingVersion; // Structured version info
  duration: number; // Duration in milliseconds
  isrc?: string; // International Standard Recording Code
  releaseDate?: string; // ISO date string
  label?: string; // Record label
  isExplicit?: boolean; // Explicit content flag
  bpm?: number; // Beats per minute if known
  key?: string; // Musical key if known
  audioFingerprint?: string; // Acoustic fingerprint hash if computed
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Structured version information for a recording.
 * Critical for remix-sensitive matching.
 */
export interface RecordingVersion {
  type: VersionType;
  name?: string; // "Radio Edit", "Extended Mix", "VIP"
  remixer?: string; // "Skrillex", "Tiësto"
  isClean?: boolean; // Clean/radio version
  isLive?: boolean;
  isAcoustic?: boolean;
  isInstrumental?: boolean;
  isAccapella?: boolean;
  year?: number; // For remaster identification
  extraInfo?: string; // Catch-all for unparsed version text
}

export type VersionType =
  | 'original' // Original release
  | 'radio_edit' // Shorter radio version
  | 'extended' // Extended/club version
  | 'remix' // Third-party remix
  | 'vip' // Artist's own rework
  | 'bootleg' // Unofficial remix
  | 'mashup' // Combined with other tracks
  | 'edit' // DJ edit (intro/outro mods)
  | 'remaster' // Remastered version
  | 'live' // Live recording
  | 'acoustic'
  | 'instrumental'
  | 'acapella'
  | 'cover' // Cover version by different artist
  | 'unknown';

/**
 * Asset represents a provider-specific reference to a recording.
 * One recording can have multiple assets across providers.
 */
export interface Asset {
  id: string; // Internal ID
  recordingId: string; // Reference to matched Recording
  provider: AssetProvider;
  providerId: string; // Provider's track ID
  providerUri?: string; // Full URI (spotify:track:xxx)
  localPath?: string; // For local files
  metadata: AssetMetadata; // Raw metadata from provider
  matchResult?: MatchResult; // How this was matched
  userOverride?: boolean; // User manually confirmed/corrected
  createdAt: Date;
  updatedAt: Date;
}

export type AssetProvider =
  | 'spotify'
  | 'tidal'
  | 'apple'
  | 'apple_music'
  | 'deezer'
  | 'amazon_music'
  | 'youtube_music'
  | 'beatport'
  | 'serato'
  | 'rekordbox'
  | 'virtualdj'
  | 'traktor'
  | 'local_file';

/**
 * Raw metadata as returned by the provider.
 * Preserved for audit trail and re-matching.
 */
export interface AssetMetadata {
  title: string;
  artist: string;
  artists?: string[];
  album?: string;
  duration?: number; // milliseconds
  isrc?: string;
  releaseDate?: string;
  isExplicit?: boolean;
  popularity?: number;
  previewUrl?: string;
  albumArt?: string;
  // Provider-specific fields
  raw?: Record<string, unknown>;
}

// =============================================================================
// MATCHING SYSTEM
// =============================================================================

/**
 * Mode of matching - determines scoring weights and thresholds.
 */
export type MatchMode = 'official_release' | 'remix_sensitive';

/**
 * Result of a matching attempt with full evidence.
 */
export interface MatchResult {
  candidateId: string; // Provider's track ID
  provider: AssetProvider;
  confidence: number; // 0-100 confidence score
  mode: MatchMode;
  isMatch: boolean; // Meets threshold for auto-match
  requiresReview: boolean; // Needs user confirmation
  evidence: MatchEvidence;
  reasons: MatchReason[];
  alternativeCandidates?: MatchCandidate[];
  matchedAt: Date;
}

/**
 * Detailed evidence for why a match was made or rejected.
 */
export interface MatchEvidence {
  // Title matching
  titleScore: number; // 0-100
  titleNormalized: {
    source: string;
    candidate: string;
    similarity: number;
  };

  // Artist matching
  artistScore: number; // 0-100
  artistsMatched: string[];
  artistsMissing: string[];
  artistUnavailable?: boolean; // True if artist data wasn't available for comparison

  // ISRC matching (when available)
  isrcMatch?: boolean;
  isrcSource?: string;
  isrcCandidate?: string;

  // Duration matching
  durationScore: number; // 0-100
  durationDelta: number; // milliseconds difference
  durationSource?: number;
  durationCandidate?: number;
  durationUnavailable?: boolean; // True if duration data wasn't available for comparison

  // Version/remix matching
  versionScore: number; // 0-100
  versionSource?: RecordingVersion;
  versionCandidate?: RecordingVersion;
  versionConflict?: string; // Explanation of conflict

  // Explicit/clean flag
  explicitMatch?: boolean;

  // Release date matching
  releaseDateMatch?: boolean;
  releaseDateDelta?: number; // days difference

  // Audio fingerprint (when available)
  fingerprintMatch?: 'exact' | 'near' | 'partial' | 'none';
  fingerprintConfidence?: number;

  // Penalties applied
  penalties: MatchPenalty[];
}

/**
 * Penalty applied during matching.
 */
export interface MatchPenalty {
  type: PenaltyType;
  value: number; // Points deducted
  reason: string;
}

export type PenaltyType =
  | 'version_mismatch' // Original vs Remix confusion
  | 'duration_delta' // Large duration difference
  | 'artist_mismatch' // Wrong artist
  | 'remixer_mismatch' // Wrong remixer
  | 'explicit_mismatch' // Clean vs Explicit
  | 'fingerprint_mismatch' // Audio doesn't match
  | 'suspicious_title' // Karaoke/cover/tribute markers
  | 'low_popularity' // Very low popularity (possible bootleg)
  | 'label_mismatch'; // Different record label

/**
 * Human-readable reason for match decision.
 */
export interface MatchReason {
  type: 'positive' | 'negative' | 'warning';
  code: string;
  message: string;
}

/**
 * A candidate match with scoring info.
 */
export interface MatchCandidate {
  providerId: string;
  provider: AssetProvider;
  metadata: AssetMetadata;
  confidence: number;
  isTopChoice: boolean;
  evidence: MatchEvidence;
  reasons: MatchReason[];
}

// =============================================================================
// CONFLICT RESOLUTION
// =============================================================================

/**
 * When multiple candidates are viable, present a conflict for resolution.
 */
export interface MatchConflict {
  id: string;
  sourceTrack: {
    title: string;
    artist: string;
    duration?: number;
    version?: RecordingVersion;
  };
  candidates: MatchCandidate[];
  conflictType: ConflictType;
  suggestedAction: 'auto_select' | 'user_review' | 'skip';
  autoSelectedId?: string;
  resolvedAt?: Date;
  resolution?: ConflictResolution;
}

export type ConflictType =
  | 'original_vs_remix' // Can't determine if original or remix
  | 'multiple_versions' // Multiple viable versions exist
  | 'artist_ambiguity' // Same song, different artists (covers)
  | 'low_confidence' // No high-confidence match
  | 'no_match'; // No viable candidates found

export interface ConflictResolution {
  selectedCandidateId?: string;
  action: 'select' | 'skip' | 'manual_link' | 'create_rule';
  userId?: string;
  resolvedAt: Date;
  notes?: string;
}

// =============================================================================
// USER RULES & OVERRIDES
// =============================================================================

/**
 * User-defined matching rules that persist and apply to future matches.
 */
export interface MatchRule {
  id: string;
  type: RuleType;
  condition: RuleCondition;
  action: RuleAction;
  createdBy: string;
  createdAt: Date;
  appliedCount: number;
}

export type RuleType =
  | 'always_link' // Always link these two recordings
  | 'never_link' // Never link (e.g., original ≠ remix)
  | 'prefer_version' // Prefer specific version type
  | 'prefer_provider' // Prefer matches from specific provider
  | 'artist_alias'; // Artist name alias mapping

export interface RuleCondition {
  field: string; // title, artist, isrc, etc.
  operator: 'equals' | 'contains' | 'matches' | 'not_equals';
  value: string;
  sourceProvider?: AssetProvider;
  targetProvider?: AssetProvider;
}

export interface RuleAction {
  type: 'link' | 'block' | 'prefer' | 'transform';
  targetId?: string;
  transformFn?: string; // Serialized transformation
}

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Configurable thresholds and weights for matching.
 */
export interface MatcherConfig {
  // Score weights (should sum to 100)
  weights: {
    title: number; // Default: 25
    artist: number; // Default: 25
    duration: number; // Default: 20
    version: number; // Default: 15
    isrc: number; // Default: 10 (bonus when available)
    fingerprint: number; // Default: 5 (bonus when available)
  };

  // Thresholds
  thresholds: {
    autoMatchMin: number; // Default: 85 - Auto-accept above this
    reviewMin: number; // Default: 60 - Suggest for review above this
    rejectMax: number; // Default: 40 - Auto-reject below this
  };

  // Duration tolerance
  duration: {
    exactToleranceMs: number; // Default: 1000 (1 second)
    closeToleranceMs: number; // Default: 3000 (3 seconds)
    remixMaxDeltaMs: number; // Default: 10000 (10 seconds) - Hard block for remix mode
  };

  // Version matching
  version: {
    penaltyMismatch: number; // Default: 30 - Deduct for version type mismatch
    penaltyRemixerMismatch: number; // Default: 50 - Deduct for different remixer
    blockOnVersionConflict: boolean; // Default: true - Hard block original vs remix
  };

  // Bad version detection
  badVersionMarkers: string[]; // Karaoke, tribute, cover, etc.

  // Candidate limits
  maxCandidates: number; // Default: 5 - Keep top N per provider
}

/**
 * Default configuration.
 */
export const DEFAULT_MATCHER_CONFIG: MatcherConfig = {
  weights: {
    title: 25,
    artist: 25,
    duration: 20,
    version: 15,
    isrc: 10,
    fingerprint: 5,
  },
  thresholds: {
    autoMatchMin: 85,
    reviewMin: 60,
    rejectMax: 40,
  },
  duration: {
    exactToleranceMs: 1000,
    closeToleranceMs: 3000,
    remixMaxDeltaMs: 10000,
  },
  version: {
    penaltyMismatch: 30,
    penaltyRemixerMismatch: 50,
    blockOnVersionConflict: true,
  },
  badVersionMarkers: [
    'karaoke',
    'tribute',
    'made famous by',
    'made popular by',
    'originally performed by',
    'in the style of',
    'cover version',
    'instrumental version',
    'backing track',
    '8-bit',
    'lullaby',
    'music box',
  ],
  maxCandidates: 5,
};
